/// <amd-module name="VSS/Features/Frame/CommandBar/CommandBar" />
import React = require("react");
import ReactDOM = require("react-dom");
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { IVssContributionService } from "VSS/Platform/Contribution";
import {
    css,
    IVssComponentContext,
    IVssComponentDetail,
    IVssComponentProperties,
    IVssLayoutManager,
    VssComponent,
    WrappedComponent
} from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { contains } from "VSS/Platform/Util/DOM";
import { IVssContextualMenuItemProvider, IVssContextualMenuProps } from "VSSUI/Components/VssContextualMenu/VssContextualMenu.Props";
import { Icon } from "VSSUI/Icon";
import { ContributedMenuItemProvider } from "../ContributedMenuItemProvider";
import { IResponsiveLayoutProps, ResponsiveLayout, ResponsiveOrientation } from "../ResponsiveLayout";
import {
    ICommandBarCommandProps,
    ICommandBarFlyoutProps,
    ICommandBarItemGroup,
    ICommandBarMenuProps,
    ICommandBarProps,
    ICommandBarSeparatorProps,
    IComponentWithExpandableProps,
    IComponentWithState,
    IComponentWithStateProps,
    LayoutOrientation
} from "./CommandBar.Props";
import { CommandBarFlyout } from "./CommandBarFlyout";

export class CommandBarComponent extends VssComponent<ICommandBarProps, {}> {
    public static componentType = "commandBar";

    private componentDetails?: IVssComponentDetail[];
    private stateComponents: { [key: string]: IComponentWithState } = {};
    private components: { [key: string]: IComponentWithState } = {};
    private childCount: number = 0;

    private responsiveProps?: IResponsiveLayoutProps;
    private responsiveLayout: ResponsiveLayout | null;

    private processFocusChange?: boolean;
    private focusElement?: HTMLDivElement | null;
    private expandedKey?: string;
    private focusedKey?: string;

    private expandedReason?: string;
    private expanded?: boolean;

    public constructor(props: ICommandBarProps, context: IVssComponentContext) {
        super(props, context);

        this.processItems(props);
    }

    public render(): JSX.Element {
        // Determine the number of children our CommandBar contains.
        if (this.componentDetails) {
            this.childCount = this.componentDetails.length;
        } else if (this.props.children) {
            this.childCount = React.Children.count(this.props.children);
        }

        // Create the list of direct command bar children
        const commandBarItems: { groupKey: string; element: JSX.Element }[] = [];

        if (this.componentDetails) {
            this.componentDetails.forEach((item, index) => {
                commandBarItems.push({
                    groupKey: item.componentProperties && item.componentProperties["groupKey"],
                    element: (
                        <WrappedComponent
                            wrappedType={item.componentType}
                            {...Object.assign(this.getItemProps(index.toString()), item.componentProperties, { key: index.toString() })}
                        />
                    )
                });
            }, this);
        } else if (this.props.children) {
            React.Children.forEach(this.props.children, (childComponent: React.ReactElement<any>, index: number) => {
                let localProps: IVssComponentProperties;

                // If a "null" component was added to the commandbar we will add a placeholder div.
                if (!childComponent) {
                    childComponent = <div className="commandbar-item-placeholder" />;
                    localProps = {};
                }

                if (typeof childComponent.type === "string") {
                    // For non-component based children we only want to return the reference. The commandbar
                    // wont attempt to interact with them at runtime except managing focus based on tabIndex.
                    localProps = {
                        ref: (itemComponent: IComponentWithState) => {
                            this.components[index.toString()] = itemComponent;
                        }
                    };
                } else {
                    // For component based children we will pass it the IComponentWithProps. This will allow the
                    // the child components to interact with the command bar state.
                    localProps = this.getItemProps(index.toString());
                }

                commandBarItems.push({
                    groupKey: childComponent.props && (childComponent.props["groupKey"] || childComponent.props["data-groupKey"]),
                    element: React.cloneElement(childComponent, { ...localProps, ...childComponent.props }, childComponent.props.children)
                });
            });
        }

        let commandBarItemElements: JSX.Element[];

        // Organize items into groups if groups were supplied
        if (this.props.groups) {
            // Build a lookup of groups by key
            const groupsByKey: { [key: string]: ICommandBarItemGroup } = {};
            for (const group of this.props.groups) {
                groupsByKey[group.key] = group;
            }

            const groupedItems: { group: ICommandBarItemGroup | undefined; items: React.ReactElement<any>[] }[] = [];
            let currentGroup: { group: ICommandBarItemGroup | undefined; items: React.ReactElement<any>[] } | undefined = undefined;

            // Go through the items in order and create groups as necessary
            for (const commandBarItem of commandBarItems) {
                let group: ICommandBarItemGroup | undefined = undefined;
                if (commandBarItem.groupKey) {
                    group = groupsByKey[commandBarItem.groupKey];
                }

                if (currentGroup && group === currentGroup.group) {
                    // Add the item to the end of the same group as the previous item
                    currentGroup.items.push(commandBarItem.element);
                } else {
                    // First item or a different group from the previous item.
                    let existingGroup: { group: ICommandBarItemGroup | undefined; items: React.ReactElement<any>[] } | undefined = undefined;
                    if (group) {
                        // For explicit groups, try to find a previous entry with the same group key to group these items together.
                        existingGroup = groupedItems.filter(g => g.group === group)[0];
                    }

                    if (existingGroup) {
                        currentGroup = existingGroup;
                    } else {
                        currentGroup = {
                            group: group,
                            items: []
                        };
                        groupedItems.push(currentGroup);
                    }

                    currentGroup.items.push(commandBarItem.element);
                }
            }

            commandBarItemElements = [];

            let itemsSinceLastSeparator = false;
            let separatorPending = false;
            let groupSeparatorCount = 0;

            for (const groupedItemsEntry of groupedItems) {
                if (groupedItemsEntry.group) {
                    if (itemsSinceLastSeparator && groupedItemsEntry.group.useSeparators) {
                        commandBarItemElements.push(<CommandBarSeperatorComponent key={"group-separator-" + (++groupSeparatorCount).toString()} />);
                    }

                    commandBarItemElements.push(
                        <div
                            key={"group-" + groupedItemsEntry.group.key}
                            className={groupedItemsEntry.group.className}
                            ref={groupedItemsEntry.group.ref}
                        >
                            {groupedItemsEntry.items}
                        </div>
                    );

                    if (groupedItemsEntry.group.useSeparators) {
                        separatorPending = true;
                        itemsSinceLastSeparator = false;
                    } else {
                        itemsSinceLastSeparator = true;
                    }
                } else {
                    if (separatorPending) {
                        commandBarItemElements.push(<CommandBarSeperatorComponent key={"group-separator-" + (++groupSeparatorCount).toString()} />);
                    }
                    commandBarItemElements.push(...groupedItemsEntry.items);
                    itemsSinceLastSeparator = true;
                }
            }
        } else {
            commandBarItemElements = commandBarItems.map(i => i.element);
        }

        // Build the commandbar with the root element and then the list of children either from componentDetails
        // or the React.Children associated with this component tree.
        let childComponents = (
            <div
                aria-label={this.props["aria-label"]}
                className={css(
                    "commandbar",
                    this.props.className,
                    this.props.componentRegion ? "region-" + this.props.componentRegion : "",
                    this.props.orientation === LayoutOrientation.Column ? "flex-column" : "flex-row",
                    this.focusedKey ? "focus" : ""
                )}
                data-renderedregion={this.props.componentRegion ? this.props.componentRegion : null}
                ref={componentElement => {
                    this.componentElement = componentElement;
                }}
                role="menubar"
                onFocus={this.onFocus}
                onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                    this.onKeyDown(event.nativeEvent);
                }}
            >
                <div
                    key="focus.element"
                    ref={focusElement => {
                        this.focusElement = focusElement;
                    }}
                    onFocus={this.onFocus}
                    tabIndex={this.focusedKey ? -1 : 0}
                />
                {commandBarItemElements}
            </div>
        );

        if (this.responsiveProps) {
            childComponents = (
                <ResponsiveLayout
                    ref={responsiveLayout => {
                        this.responsiveLayout = responsiveLayout;
                    }}
                    orientation={
                        this.props.orientation === LayoutOrientation.Column ? ResponsiveOrientation.Vertical : ResponsiveOrientation.Horizontal
                    }
                    {...this.responsiveProps}
                >
                    {childComponents}
                </ResponsiveLayout>
            );
        }

        return childComponents;
    }

    public componentDidUpdate(previousProps: ICommandBarProps, previousState: {}): void {
        super.componentDidUpdate(previousProps, previousState);

        if (this.processFocusChange) {
            // console.log("focus Change");

            let focusFound = false;

            // Loop through the set of components and find the component that contains the focused element.
            for (const componentKey in this.components) {
                const childComponent = this.components[componentKey];
                if (childComponent) {
                    const componentElement = ReactDOM.findDOMNode(childComponent) as Element;

                    if (componentElement && document.activeElement && contains(componentElement, document.activeElement)) {
                        if (this.focusedKey !== componentKey) {
                            // console.log("focus " + componentKey);

                            if (document.activeElement.getAttribute("tabIndex") === "-2") {
                                // console.log("focus IN");

                                this.addEventListener(document.body, "blur", this.onBlur, true);

                                // Ignore Left/Right arrow movement in IE 11 since it doesnt work properly.
                                if (navigator.userAgent.toLowerCase().indexOf("trident") === -1) {
                                    this.addEventListener(document.body, "keydown", this.onKeyDownBody);
                                }

                                // If we are expanded when this element gets focus, we will expand the next element
                                // otherwise we will
                                if (this.expanded) {
                                    this.expand(componentKey, true, "focus");
                                }

                                // Focus was found in a new component that is managed via the commandbar.
                                focusFound = true;
                                this.focusedKey = componentKey;

                                this.forceUpdate();
                            }
                        } else {
                            // Focus remains in the previously managed component.
                            focusFound = true;
                        }

                        break;
                    }
                }
            }

            if (!focusFound) {
                // console.log("blur OUT");

                if (this.expandedKey) {
                    // console.log("blur collapse - " + this.expandedKey);

                    this.expand(this.expandedKey, false, "blur");
                }

                if (this.focusedKey) {
                    this.focusedKey = undefined;
                    this.removeEventListener(document.body, "blur", this.onBlur, true);
                    this.removeEventListener(document.body, "keydown", this.onKeyDownBody);
                    this.forceUpdate();
                }
            }

            delete this.processFocusChange;
        }
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Readonly<{ children?: React.ReactNode }> & Readonly<ICommandBarProps>): void {
        this.processItems(nextProps);

        // Make sure the focus key exists in the next props.
        if (this.focusedKey && (nextProps.children || this.componentDetails)) {
            const currentComponent = this.components[this.focusedKey];
            if (currentComponent) {
                let focusKeyFound = false;

                // Look through either the react children or the component details passed in.
                if (nextProps.children) {
                    React.Children.map(this.props.children, (childComponent: React.ReactElement<any>, index: number) => {
                        if (
                            childComponent.key === currentComponent.props.key ||
                            childComponent.key === currentComponent.props.navigationId ||
                            (currentComponent.props.contributedNavigation && childComponent.key === currentComponent.props.contributedNavigation.id)
                        ) {
                            focusKeyFound = true;
                        }
                    });
                } else if (this.componentDetails) {
                    for (let i = 0; i < this.componentDetails.length; i++) {
                        const detail = this.componentDetails[i];
                        if (detail.componentProperties && detail.componentProperties.key === currentComponent.props.key) {
                            focusKeyFound = true;
                            break;
                        }
                    }
                }

                // If currently focused key exists, then we are done.  If not, then
                // try and set focus to another item in the command bar
                if (!focusKeyFound) {
                    let nextComponent: IComponentWithState;
                    let currentIndex: number = parseInt(this.focusedKey);
                    const startIndex: number = currentIndex;

                    while (true) {
                        nextComponent = this.components[currentIndex];
                        if (nextComponent && this.focusComponent(nextComponent)) {
                            break;
                        }

                        if (--currentIndex === -1) {
                            currentIndex = this.childCount - 1;
                        }

                        // If there are no more components to check we are done, just leave the state as is.
                        if (currentIndex === startIndex) {
                            break;
                        }
                    }
                }
            }
        }

        this.components = {};
        this.stateComponents = {};
    }

    public expand = (itemKey: string, state: boolean, reason?: string): boolean => {
        if (state) {
            if (!this.expanded || this.expandedKey !== itemKey) {
                const componentWithState = this.components[itemKey];

                if (componentWithState) {
                    // console.log("expand " + itemKey + " reason " + reason);

                    // If the commandbar has focus and we expand another item we will move the
                    // focus to the component being expanded if possible.
                    if (this.focusedKey && this.focusedKey !== itemKey) {
                        this.focusComponent(this.components[itemKey]);
                    }

                    // Save the details about the component we are expanding.
                    this.expandedReason = reason;
                    this.expandedKey = itemKey;
                    this.expanded = true;
                    this.forceUpdate();
                    return true;
                }
            }
        } else {
            if (this.expandedKey === itemKey && this.expanded) {
                // console.log("collapse " + itemKey + " reason " + reason);

                this.expandedReason = undefined;
                this.expandedKey = undefined;
                this.expanded = undefined;
                this.forceUpdate();
                return true;
            }
        }

        return false;
    };

    public focus(componentIndex: number): void {
        this.focusComponent(this.components[componentIndex.toString()]);
    }

    public resetLayout(): void {
        if (this.responsiveLayout) {
            this.responsiveLayout.resetLayout();
        }
    }

    private processItems(props: ICommandBarProps): void {
        // If a region was supplied, we will determine the set of items from the region's components.
        if (props.componentRegion) {
            const layoutManager: IVssLayoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            this.componentDetails = layoutManager.getComponentsForRegion(props.componentRegion as string).value;
        } else if (props.items) {
            this.componentDetails = props.items;
        } else {
            this.componentDetails = undefined;
        }

        // We add a component to the front of the children to manage focus, because of this
        // we need to adjust all the indexes down by one for the adjustments and ignored children.
        if (props.responsiveProps) {
            this.responsiveProps = {
                adjustments: [],
                ignoredChildren: props.responsiveProps.ignoredChildren.slice(0, props.responsiveProps.ignoredChildren.length),
                onLayoutChange: props.responsiveProps.onLayoutChange
            };

            for (let adjustmentIndex = 0; adjustmentIndex < props.responsiveProps.adjustments.length; adjustmentIndex++) {
                this.responsiveProps.adjustments[adjustmentIndex] = { ...props.responsiveProps.adjustments[adjustmentIndex] };
                this.responsiveProps.adjustments[adjustmentIndex].index++;
            }

            this.responsiveProps.ignoredChildren.splice(0, 0, ...[true]);
        }
    }

    private focusComponent(component: IComponentWithState, focusLastComponent?: boolean): boolean {
        const childElement = ReactDOM.findDOMNode(component) as HTMLElement;
        if (childElement) {
            let focusElement: HTMLElement;

            if (childElement.getAttribute("tabIndex") === "-2") {
                focusElement = childElement;
            } else if (focusLastComponent) {
                const focusElements = childElement.querySelectorAll("[tabIndex='-2']");
                focusElement = focusElements.item(focusElements.length - 1) as HTMLElement;
            } else {
                focusElement = childElement.querySelector("[tabIndex='-2']") as HTMLElement;
            }

            if (focusElement) {
                focusElement.focus();
                return true;
            }
        }

        return false;
    }

    private getItemProps(itemKey: string): IComponentWithStateProps {
        return {
            itemKey: itemKey,
            expand: this.expand,
            expandOnHover: true,
            expanded: this.expandedKey === itemKey,
            expandedReason: this.expandedReason,
            mouseHoverDelayMs: this.expanded ? 0 : 200,
            ref: (itemComponent: IComponentWithState) => {
                this.components[itemKey] = this.stateComponents[itemKey] || itemComponent;
            },
            selected: this.focusedKey === itemKey,
            shouldFocusOnMount: !!this.focusedKey
        };
    }

    private onBlur = (event: FocusEvent): void => {
        // console.log("blur");

        // Capture only when we leave the commandbar, ignoring focus events in flyouts
        // They don't live on the same DOM tree, so contains check would fail later in componentDidUpdate.
        if (this.componentElement && this.componentElement.contains(event.srcElement as Element)) {
            // We need to delay the update from the blur since this will occur before the focus
            // happens on the next child component, this will cause expansion to collapse.
            this.setTimeout(() => {
                this.processFocusChange = true;
                this.forceUpdate();
            });
        }
    };

    private onFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
        // If the commandbar itself is getting focus we will select the first element
        if (this.focusElement === document.activeElement) {
            // Determine the first element in the command bar that should recieve focus.
            const focusElement = this.componentElement!.querySelector("[tabIndex='-2']") as HTMLElement;
            if (focusElement) {
                focusElement.focus();
            }
        } else {
            // console.log("focus");
            if (this.componentElement && this.componentElement.contains(event.target as Element)) {
                this.processFocusChange = true;
                this.forceUpdate();
            }
        }
    };

    private onKeyDown = (event: KeyboardEvent): void => {
        if (!event.defaultPrevented && this.focusedKey) {
            let nextComponent: IComponentWithState;
            let currentIndex: number = parseInt(this.focusedKey);
            const startIndex: number = currentIndex;
            let focusLastComponent = false;

            // Determine the key's to be used for movement based on the orientation.
            const keyPrevious = this.props.orientation === LayoutOrientation.Column ? 38 /* KeyCode.UpArrow */ : 37 /* KeyCode.LeftArrow */;
            const keyNext = this.props.orientation === LayoutOrientation.Column ? 40 /* KeyCode.DownArrow */ : 39 /* KeyCode.RightArrow */;

            while (true) {
                if (event.which === keyPrevious) {
                    if (--currentIndex === -1) {
                        currentIndex = this.childCount - 1;
                    }
                    focusLastComponent = true;
                } else if (event.which === keyNext) {
                    if (++currentIndex === this.childCount) {
                        currentIndex = 0;
                    }
                }

                // If there are no more components to check we are done, just leave the state as is.
                if (currentIndex === startIndex) {
                    break;
                }

                nextComponent = this.components[currentIndex];
                if (nextComponent && this.focusComponent(nextComponent, focusLastComponent)) {
                    // console.log("keydown nextComponent " + currentIndex);
                    event.preventDefault();
                    break;
                }
            }
        }
    };

    private onKeyDownBody = (event: KeyboardEvent): void => {
        // This is a native event handler and will fire before the react event handler on children, so we wait.
        this.setTimeout(() => {
            this.onKeyDown(event);
        }, 0);
    };
}

VssComponent.register(CommandBarComponent.componentType, CommandBarComponent);

// @TODO: Back-compat for old component name need to fix them all to be consistent.
VssComponent.register("commandbar", CommandBarComponent);

/**
 * CommandBarSeperator component is used to draw a seperator into a commandbar.
 */
export class CommandBarSeperatorComponent extends React.Component<ICommandBarSeparatorProps, {}> {
    public static componentType = "commandBarSeperator";

    public render(): JSX.Element {
        return (
            <div className="flex separator" role="separator">
                <div className={css("flex-cell", "separator-item", this.props.className)} />
            </div>
        );
    }
}

VssComponent.register("commandBarSeparator", CommandBarSeperatorComponent);
VssComponent.register(CommandBarSeperatorComponent.componentType, CommandBarSeperatorComponent);

export interface IVerticalHeaderCommandBarSeparatorState {
    searchExpanded: boolean;
}

/**
 * Standard CommandBar child component that will handle much of the basic rendering of an item
 * container if the component doesnt need special handling.
 */
export class CommandBarChildComponent extends VssComponent<IComponentWithStateProps, {}> {
    public render(): JSX.Element {
        return (
            <div
                aria-label={this.props["aria-label"]}
                className={css("commandbar-item", this.props.className, this.props.selected || this.props.expanded ? " selected" : "")}
                id={this.props.id}
                role={"menuitem"}
                tabIndex={!this.props.noTabIndex ? (this.props.selected ? 0 : -2) : undefined}
            >
                {this.props.children}
            </div>
        );
    }
}

/**
 * CommandBar child component that follows a link or executes a command when activated.
 */
export class CommandBarCommandComponent extends VssComponent<ICommandBarCommandProps, {}> {
    public static componentType = "commandBarCommand";

    private href?: string;

    public constructor(props: ICommandBarCommandProps, context: IVssComponentContext) {
        super(props, context);

        this.computeHref(props);
    }

    public render(): JSX.Element {
        const isLink = !!this.href;

        const className = css("commandbar-item commandbar-item-command", this.props.className);
        const commonProps = {
            "aria-label": this.props["aria-label"],
            id: this.props.id,
            role: "menuitem",
            tabIndex: !this.props.noTabIndex ? (this.props.selected ? 0 : -2) : undefined
        };

        const children: React.ReactNode[] = [];

        if (this.props.iconName) {
            children.push(<Icon key="icon" className="commandbar-item-icon" iconName={this.props.iconName} />);
        }

        if (this.props.text) {
            children.push(
                <span key="text" className="commandbar-item-text">
                    {this.props.text}
                </span>
            );
        }

        children.splice(children.length, 0, this.props.children);

        if (isLink) {
            return (
                <FPSLinkComponent
                    {...commonProps}
                    className={css("commandbar-item-link", className, this.props.selected || this.props.expanded ? " selected" : "")}
                    href={this.href}
                    supportsFPS={this.props.supportsFPS === true}
                    title={this.props.title}
                    onClick={this.props.onClick}
                    onMouseDown={event => {
                        event.preventDefault();
                    }}
                    telemetrySource={this.props.telemetrySource || "commandBarCommand"}
                    telemetryProperties={this.props.telemetryProperties}
                    navigationId={this.props.navigationId}
                >
                    {children}
                </FPSLinkComponent>
            );
        } else {
            return (
                <div
                    {...commonProps}
                    className={css("commandbar-item-button cursor-pointer", className, this.props.selected ? "selected" : "")}
                    role="button"
                    title={this.props.title}
                    onClick={this.onDivClick}
                    onKeyDown={this.onDivKeyDown}
                    onMouseDown={event => {
                        event.preventDefault();
                    }}
                >
                    {children}
                </div>
            );
        }
    }

    public UNSAFE_componentWillReceiveProps(newProps: IComponentWithStateProps): void {
        this.computeHref(newProps);
    }

    private computeHref(props: IComponentWithStateProps) {
        if (props.href) {
            this.href = props.href;
        } else if (props.routeId) {
            this.href = routeUrl(this.context.pageContext, props.routeId, props.routeValues || {});
        } else {
            this.href = undefined;
        }
    }

    private onDivClick = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
        if (!event.isDefaultPrevented()) {
            if (this.props.telemetrySource) {
                const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService.publishEvent("WebPlatform", "Command", {
                    ...(this.props.telemetryProperties || {}),
                    source: this.props.telemetrySource
                });
            }

            if (this.props.onClick) {
                this.props.onClick(event);
            }
        }
    };

    private onDivKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!event.isDefaultPrevented()) {
            if (event.which === 32 /* space */ || event.which === 13 /* enter */) {
                this.onDivClick(event);
            }
        }
    };
}

VssComponent.register("commandBarLink", CommandBarCommandComponent);
VssComponent.register(CommandBarCommandComponent.componentType, CommandBarCommandComponent);

export class CommandBarExpandableComponent extends VssComponent<IComponentWithExpandableProps, {}> implements IComponentWithState {
    private hoverTimeout?: number;
    private element: HTMLDivElement | null;

    public render(): JSX.Element {
        return (
            <div
                aria-controls={this.props.expanded ? this.props.calloutId : undefined}
                aria-expanded={this.props.expanded}
                aria-haspopup={true}
                aria-label={this.props["aria-label"]}
                aria-owns={this.props.expanded ? this.props.calloutId : undefined}
                className={css(
                    "commandbar-item cursor-pointer",
                    this.props.className,
                    this.props.selected || this.props.expanded ? "selected" : "",
                    this.props.expanded ? "expanded" : ""
                )}
                id={this.props.id}
                ref={element => (this.element = element)}
                role="menuitem"
                tabIndex={!this.props.noTabIndex ? (this.props.selected ? 0 : -2) : undefined}
                onClick={this.onClick}
                onMouseDown={event => {
                    if (this.element && this.element.contains(event.target as Element)) {
                        // Prevent focus from going to commandbar component when clicked by the mouse.
                        // In Horizontal nav L2, it caused the previous hub to be highlighted #1334389
                        event.preventDefault();
                    }
                }}
                onKeyDown={this.onKeyDown}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
                onTouchEnd={this.touchEnd}
            >
                {this.props.iconName && <Icon iconName={this.props.iconName} />}
                {this.props.children}
            </div>
        );
    }

    protected onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (this.element && this.element.contains(event.target as Element)) {
            if (!event.defaultPrevented) {
                const expandKey = this.props.expandKey || 40; /* KeyCode.DownArrow */
                if (
                    event.which === 13 /* KeyCode.Enter */ ||
                    event.which === expandKey ||
                    (event.shiftKey && event.which === 121) /* KeyCode.F10 */
                ) {
                    if (this.props.expand && this.props.itemKey) {
                        this.props.expand(this.props.itemKey, true, event.type);
                    }

                    event.preventDefault();
                }
            }
        }
    };

    protected onMouseEnter = (event: React.MouseEvent<HTMLDivElement>): void => {
        if (this.props.expandOnHover && this.props.expand) {
            const eventType = event.type;

            if (this.hoverTimeout) {
                this.clearTimeout(this.hoverTimeout);
            }

            this.hoverTimeout = this.setTimeout(() => {
                if (this.props.expand && this.props.itemKey) {
                    this.props.expand(this.props.itemKey, true, eventType);
                }
            }, this.props.mouseHoverDelayMs);
        }
    };

    protected onMouseLeave = (event: React.MouseEvent<HTMLDivElement>): void => {
        if (this.hoverTimeout) {
            this.clearTimeout(this.hoverTimeout);
            delete this.hoverTimeout;
        }
    };

    protected onClick = (event: React.MouseEvent<HTMLDivElement>): void => {
        if (this.element && this.element.contains(event.target as Element)) {
            if (this.props.expand && this.props.itemKey && !event.defaultPrevented) {
                this.props.expand(this.props.itemKey, !this.props.expanded, event.type);
            }
        }
    };

    protected touchEnd = (event: React.TouchEvent<HTMLDivElement>): void => {
        if (this.props.expand && this.props.itemKey && !this.props.expanded) {
            this.props.expand(this.props.itemKey, true, event.type);
            event.preventDefault(); // Prevents Click events from firing, including in internal members (no navigation)
        }
    };
}

/**
 * CommandBarMenuComponent is used to create an element in a command bar with a menu attached to it.
 */
export class CommandBarMenuComponent extends VssComponent<ICommandBarMenuProps, {}> implements IComponentWithState {
    public static defaultProps = {
        ...({ mouseLeaveDelayMs: 500, mouseHoverDelayMs: 200 } as Partial<IComponentWithExpandableProps>),
        ...VssComponent.defaultProps
    };

    private menuComponent?: { contains?: (element?: Element) => boolean };
    private menuId?: string;

    public constructor(props: ICommandBarMenuProps) {
        super(props);

        if (props.menuProps) {
            this.menuId = props.menuProps.id;
        }

        this.menuId = this.menuId || "menu" + this.componentId;
    }

    public render(): JSX.Element {
        const menuProps = this.props.expanded ? this.getMenuProps() : null;

        return (
            <CommandBarExpandableComponent
                {...this.props}
                calloutId={this.props.expanded ? this.menuId : undefined}
                className={css("commandbar-item-menu", this.props.className)}
                id={"cmec" + this.componentId}
            >
                {this.props.children}
                {this.props.expanded ? (
                    <WrappedComponent
                        wrappedType="contextualMenu"
                        wrappedRef={menuComponent => {
                            this.menuComponent = menuComponent;
                        }}
                        wrappedProps={menuProps}
                        dependencies={["ms.vss-features.ui-content"]}
                    />
                ) : null}
            </CommandBarExpandableComponent>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        // Since this is the first pass, if we are expanded we need to force an update now
        // that we have a componentElement to target the menu too.
        if (this.props.expanded) {
            this.forceUpdate();
        }
    }

    private getMenuProps(): IVssContextualMenuProps {
        const menuItemProviders: IVssContextualMenuItemProvider[] = [];

        if (this.props.menuId) {
            menuItemProviders.push(new ContributedMenuItemProvider(this.context.pageContext, this.props.menuId));
        }

        if (this.props.menuProps && this.props.menuProps.providers) {
            menuItemProviders.push(...this.props.menuProps.providers);
        }

        return {
            // Default the menu to still adjacent the parent element.
            gapSpace: 0,

            // Ensure the menu has an Id, it is needed for defining aria-owns when shown.
            id: this.menuId,

            // If the caller supplied a direct set of items we will use them.
            items: this.props.items || [],

            // If contributed providers were supplied we will use them.
            providers: menuItemProviders,

            // Force the menu all the way to the edge of the window, otherwise it has built in padding from
            // the left and right sides.
            bounds: {
                left: 0,
                top: 0,
                right: window.innerWidth,
                bottom: window.innerHeight,
                width: window.innerWidth,
                height: window.innerHeight
            },
            ...this.props.menuProps,

            // If expansion is due to a mouseenter event we will set a mouseLeaveDelayMs.
            mouseLeaveDelayMs: this.props.expandedReason && this.props.expandedReason === "mouseenter" ? this.props.mouseLeaveDelayMs : 0,

            // When the menu is dismissed we will call expand with false (collapse).
            onDismiss: (reason?: Event) => {
                if (this.props.expand && this.props.itemKey) {
                    this.props.expand(this.props.itemKey, false, reason && reason.type);
                }
            },

            // We will focus the menu if the parent asked us too.
            shouldFocusOnMount: this.props.shouldFocusOnMount,

            // Use the commandbar expandable component as the location for the menu.
            target: "#cmec" + this.componentId
        };
    }
}

VssComponent.register("commandBarMenu", CommandBarMenuComponent);

/**
 * CommandBarFlyoutComponent is used to open a flyout associated with the commandbar element.
 */
export class CommandBarFlyoutComponent extends VssComponent<ICommandBarFlyoutProps, {}> implements IComponentWithState {
    public render(): JSX.Element {
        return (
            <CommandBarExpandableComponent
                {...this.props}
                expandOnHover={false}
                calloutId={this.props.expanded ? "flyout" + this.componentId : undefined}
                className={css("commandbar-item-flyout", this.props.className)}
                id={"cpec" + this.componentId}
            >
                {this.props.children}
                {this.props.expanded ? (
                    <CommandBarFlyout
                        {...this.props.commandBarFlyoutProps}
                        id={"flyout" + this.componentId}
                        onDismiss={this.onDismiss}
                        openingElement={"#cpec" + this.componentId}
                    >
                        <WrappedComponent {...this.props.wrappedComponent} expandedReason={this.props.expandedReason} />
                    </CommandBarFlyout>
                ) : null}
            </CommandBarExpandableComponent>
        );
    }

    private onDismiss = () => {
        if (this.props.expand && this.props.itemKey) {
            this.props.expand(this.props.itemKey, false);
        }
    };
}

VssComponent.register("commandBarFlyoutComponent", CommandBarFlyoutComponent);
